import AlertLevels from "constants/alert-levels";
import AlertNotice from "molecules/banners/alert-notice";
import Button from "atoms/buttons/button";
import Icon from "atoms/icons/icon";
import Loader from "atoms/loaders/loader";
import PageBlockList from "organisms/page-blocks/page-block-list/page-block-list";
import React, { useEffect, useRef, useState } from "react";
import TitleField from "molecules/page-and-notebook-meta/title-field";
import useCurrentPage from "utilities/hooks/domain/notebooks/pages/use-current-page";
import useMostRecentlyEditedOn from "utilities/hooks/domain/notebooks/pages/use-most-recently-edited-on";
import useLoading from "utilities/hooks/use-loading";
import useNotebookParams from "utilities/hooks/domain/notebooks/use-notebook-params";
import usePageActions from "pages/notebooks/use-page-actions";
import { ButtonStyles } from "atoms/constants/button-styles";
import { Icons } from "atoms/constants/icons";
import { ReadonlyContextProvider } from "utilities/contexts/readonly/readonly-context-provider";
import { RouteUtils } from "utilities/route-utils";
import { siteMap } from "internal-sitemap";
import { t } from "utilities/localization-utils";
import { Redirect, useHistory, useRouteMatch } from "react-router";
import { usePageIsViewOnly } from "pages/notebooks/use-page-is-view-only";
import { NotebookConstants } from "constants/notebook-constants";
import useCurrentIdentity from "utilities/hooks/use-current-identity";
import DeprecatedShow from "atoms/wrappers/show";
import usePageActionPermission, {
    PageAction,
} from "pages/notebooks/use-page-action-permission";
import useScrollDirection from "utilities/hooks/use-scroll-direction";
import useNotebookBreakpoints from "utilities/hooks/domain/notebooks/use-notebook-breakpoints";
import PageContextMenu from "pages/notebooks/pages/page-context-menu";
import { PageAccessModal } from "organisms/modals/page-access-modal/page-access-modal";
import { IconSizes } from "atoms/constants/icon-sizes";
import { GlobalPageShareLinkModal } from "organisms/modals/page-access-modal/global-page-share-link-modal";
import RemoteImage from "atoms/images/remote-image";
import DateUtils from "utilities/date-utils";
import useFeatureFlags from "utilities/hooks/use-feature-flags";
import ToggleButton from "atoms/forms/toggle-button";
import Tooltip, { TooltipPlacement } from "molecules/tooltips/tooltip";
import usePageEditSession from "./use-page-edit-session";
import LockedForEditingWrapper from "organisms/page-blocks/locked-for-editing/locked-for-editing-wrapper";
import useCurrentPageEditSession from "pages/notebooks/use-current-page-edit-session";
import { ToastManager } from "utilities/toast/toast-manager";

const CSS_CLASS_NAME = "c-page-page";

const PagePage = () => {
    const { notebookId, pageId } = useNotebookParams();
    const history = useHistory();
    const {
        currentPage,
        refresh,
        loading: currentPageLoading,
        rename: renamePage,
    } = useCurrentPage();
    const {
        mostRecentlyEditedOn,
        refreshMostRecentlyEditedOn,
        setMostRecentlyEditedOn,
    } = useMostRecentlyEditedOn(pageId);

    const { isViewOnly: isViewOnlyPermissions } = usePageIsViewOnly();
    const {
        isPageAccessModalOpen,
        handleOnPrintClick,
        handlePageAccessModalClose,
        handlePageAccessModalOpen,
        handlePageAccessModalSave,
        handleGlobalPageShareLinkModalClose,
        isNewlyPublicSharedPage,
    } = usePageActions(currentPage);

    const [loaded, setLoaded] = useState(false);
    const loading = useLoading(!loaded, currentPageLoading);
    const { currentIdentity } = useCurrentIdentity();
    const headerRef = useRef<HTMLElement>(null);
    const { can } = usePageActionPermission(currentPage);
    const isSharedPage = currentPage?.ownerId !== currentIdentity?.userId(); // May become more verbose as Notebooks collaboration is developed

    const { path } = useRouteMatch();
    const { isMobile } = useNotebookBreakpoints();
    const { handleScroll, hasScrolledDown } = useScrollDirection({
        isEnabled: isMobile,
    });

    const { allowNotebookCollaboration } = useFeatureFlags();

    const [isInEditMode, setIsInEditMode] = useState<boolean>(false);
    const { enableEditSession, activeSession, endEditSession } =
        usePageEditSession(isInEditMode);
    useCurrentPageEditSession({
        isInActiveEditSession:
            isInEditMode &&
            allowNotebookCollaboration &&
            currentIdentity?.userId() === activeSession?.userId,
        onSessionEnd: () => {
            if (isInEditMode) {
                ToastManager.info(
                    t("page-page--edit-session-timeout-message"),
                    {
                        closeOnClick: true,
                        autoClose: false,
                    }
                );
                setIsInEditMode(false);
            }
        },
    });
    const previousActiveSession = useRef(activeSession);
    useEffect(() => {
        const sessionReleased = previousActiveSession.current && !activeSession;
        const anotherSessionActive =
            activeSession?.userId !== currentIdentity?.userId;

        if (sessionReleased && isInEditMode && anotherSessionActive) {
            enableEditSession();
            refresh(pageId);
        }
        previousActiveSession.current = activeSession;
    }, [
        activeSession,
        currentIdentity,
        currentIdentity?.userId,
        enableEditSession,
        isInEditMode,
        loaded,
        loading,
        pageId,
        refresh,
    ]);

    const isViewOnly = allowNotebookCollaboration
        ? !isInEditMode || isViewOnlyPermissions
        : isViewOnlyPermissions;
    useEffect(() => {
        setLoaded(false);
    }, [pageId]);

    useEffect(() => {
        async function fetchData() {
            if (!loaded && currentPage?.id !== pageId) {
                await refresh(pageId);
                setLoaded(true);
            }
        }
        fetchData();
    }, [currentPage?.id, loaded, pageId, refresh]);

    useEffect(() => {
        if (currentPage?.mostRecentlyEditedOn) {
            setMostRecentlyEditedOn(new Date(currentPage.mostRecentlyEditedOn));
        } else {
            setMostRecentlyEditedOn(undefined);
        }
    }, [
        currentPage?.id,
        currentPage?.mostRecentlyEditedOn,
        setMostRecentlyEditedOn,
    ]);

    useEffect(() => {
        if (allowNotebookCollaboration) {
            const isNewPage =
                path ===
                RouteUtils.withCulture({
                    path: siteMap.notebooks.notebook.newPage,
                }).path;

            // Undefined check is to make sure it hadn't already been set by the user
            if (isNewPage && isInEditMode === undefined) {
                setIsInEditMode(true);
            }
        }
    }, [allowNotebookCollaboration, isInEditMode, path]);

    const formattedMostRecentlyEditedOnTime = DateUtils.formatCustomDate(
        mostRecentlyEditedOn?.toISOString(),
        "MM/DD/YYYY hh:mm A"
    );

    const handleShareButtonClick = () => {
        handlePageAccessModalOpen();
    };

    // #region Page Not Found

    const handleDismissNotFound = () => {
        history.push(
            RouteUtils.getUrl(siteMap.notebooks.notebook.detail, {
                notebookId: notebookId ?? 0,
            })
        );
    };
    const hasLoadedPage = pageId === currentPage?.id;
    // Redirect if user navigates to a notebook they don't own
    if (
        currentPage &&
        notebookId &&
        hasLoadedPage &&
        !(currentPage?.ownerId === currentIdentity?.userId()) &&
        !allowNotebookCollaboration &&
        notebookId !== NotebookConstants.SharedPageNotebookId
    ) {
        return (
            <Redirect
                to={RouteUtils.getUrl(siteMap.notebooks.notebook.detail, {
                    notebookId: NotebookConstants.SharedPageNotebookId,
                })}
            />
        );
    }

    if (!loading && currentPage == null) {
        return (
            <div className="c-not-found-page">
                <AlertNotice
                    buttonText={t("nav-aria-notebook-link")}
                    description={t("not-found-page-description")}
                    level={AlertLevels.Warning}
                    onClick={handleDismissNotFound}
                    title={t("not-found-page-title")}
                />
            </div>
        );
    }

    const handleAnyBlockHasUpdated = (): Promise<void> => {
        return refreshMostRecentlyEditedOn();
    };

    const handleEditModeToggle = async () => {
        !isInEditMode
            ? (await enableEditSession())
                ? setIsInEditMode(true)
                : setIsInEditMode(false)
            : (await endEditSession())
              ? setIsInEditMode(false)
              : setIsInEditMode(true);
    };
    const shouldLockTitleField =
        isInEditMode &&
        activeSession?.userId !== currentIdentity?.userId() &&
        activeSession?.pageId === currentPage?.id &&
        !activeSession?.blockId;

    // #endregion Page Not Found

    return loading ? (
        <div className={`${CSS_CLASS_NAME}__loaderWrapper`}>
            <Loader />
        </div>
    ) : (
        <ReadonlyContextProvider isReadonly={isViewOnly}>
            <section className={CSS_CLASS_NAME}>
                <div className={`${CSS_CLASS_NAME}__header-wrapper`}>
                    <header
                        ref={headerRef}
                        className={`${CSS_CLASS_NAME}__header ${hasScrolledDown && isMobile ? "-hidePageActions" : ""}`}>
                        {/* Page Title */}
                        {/* Only locks down title field (to match visual design spec if no active block id); rest of the page will need
                        another mechanism to prevent edits */}
                        <LockedForEditingWrapper
                            autoPosition={false}
                            firstName={activeSession?.user?.firstName}
                            lastName={activeSession?.user?.lastName}
                            isLocked={shouldLockTitleField}>
                            <TitleField
                                name={currentPage?.name}
                                onSave={renamePage}
                                resourceName="page"
                            />
                        </LockedForEditingWrapper>
                        {/* Meta - Last updated date, etc. */}
                        <div className={`${CSS_CLASS_NAME}__metaWrapper`}>
                            {mostRecentlyEditedOn && (
                                <div>
                                    {t("lastUpdated")}{" "}
                                    <span
                                        className={`${CSS_CLASS_NAME}__metaWrapper__date`}>
                                        {`${formattedMostRecentlyEditedOnTime}. `}{" "}
                                    </span>
                                </div>
                            )}
                            {isSharedPage && (
                                <span>
                                    {t("sharedByName", {
                                        name: currentPage?.ownerFullName,
                                    })}
                                </span>
                            )}
                        </div>
                        {allowNotebookCollaboration && (
                            <div
                                className={`${CSS_CLASS_NAME}__editToggleContainer`}>
                                <Tooltip
                                    disabled={!isViewOnlyPermissions}
                                    hideOnClick={true}
                                    durationInMs={0}
                                    placement={TooltipPlacement.Top}
                                    content={t(
                                        "page-page--no-editing-permission"
                                    )}>
                                    <div
                                        className={`${CSS_CLASS_NAME}__editToggleWrapper ${isViewOnlyPermissions && "-disabled"}`}>
                                        <label
                                            htmlFor="edit-mode"
                                            className={`${CSS_CLASS_NAME}__editToggleLabel ${isInEditMode ? "-editing" : ""}`}>
                                            {t("editMode")}
                                        </label>

                                        <ToggleButton
                                            disabled={isViewOnlyPermissions}
                                            dataTestId="page-page--toggle-button--edit-page"
                                            id="edit-mode"
                                            checked={isInEditMode ?? false}
                                            label={t("editMode")}
                                            onChange={handleEditModeToggle}
                                        />
                                    </div>
                                </Tooltip>
                            </div>
                        )}
                        {/* Custom Logo */}
                        <div className={`${CSS_CLASS_NAME}__customLogoWrapper`}>
                            {currentPage?.customLogoFileId && (
                                <RemoteImage
                                    altText={currentPage?.customLogoAltText}
                                    fileId={currentPage?.customLogoFileId}
                                    cssClassName={`${CSS_CLASS_NAME}__customLogo`}
                                />
                            )}
                        </div>
                        {/* Page Actions (print, share, copy, etc.) */}
                        <div className={`${CSS_CLASS_NAME}__pageActions`}>
                            <DeprecatedShow when={can(PageAction.Print)}>
                                <Button
                                    dataTestId="page-page--button--print-page"
                                    cssClassName={`${CSS_CLASS_NAME}__pageActionButton`}
                                    onClick={handleOnPrintClick}
                                    style={ButtonStyles.TertiaryAlt}>
                                    <Icon
                                        size={IconSizes.Small}
                                        type={Icons.Print}
                                    />
                                    {t("print")}
                                </Button>
                            </DeprecatedShow>
                            <DeprecatedShow when={can(PageAction.Share)}>
                                <Button
                                    dataTestId="page-page--button--share-page"
                                    cssClassName={`${CSS_CLASS_NAME}__pageActionButton`}
                                    onClick={handleShareButtonClick}
                                    style={ButtonStyles.TertiaryAlt}>
                                    <Icon
                                        size={IconSizes.Small}
                                        type={Icons.Share}
                                    />
                                    {t("share")}
                                </Button>
                            </DeprecatedShow>
                            <PageContextMenu
                                showLogoAction
                                showOpenAction={false}
                                showShareAction={false}
                                page={currentPage!}
                                isViewOnly={isViewOnly}
                                dataTestId="page-page--context-menu"
                            />
                        </div>
                    </header>
                </div>

                <section className={`${CSS_CLASS_NAME}__contentAreaWrapper`}>
                    <div
                        onScroll={handleScroll}
                        className={`${CSS_CLASS_NAME}__contentArea`}>
                        <PageBlockList
                            onAnyBlockInListUpdated={handleAnyBlockHasUpdated}
                            enableEditSession={enableEditSession}
                            isInEditMode={isInEditMode}
                            activeEditingSession={activeSession}
                        />
                    </div>
                </section>
            </section>
            {can(PageAction.ManageRecipientAccess) && (
                <>
                    <PageAccessModal
                        isOpen={isPageAccessModalOpen}
                        onClose={handlePageAccessModalClose}
                        pageId={currentPage?.id!}
                        onSaveSuccess={handlePageAccessModalSave}
                    />
                    <GlobalPageShareLinkModal
                        isOpen={isNewlyPublicSharedPage}
                        onClose={handleGlobalPageShareLinkModalClose}
                        pageId={currentPage?.id!}
                    />
                </>
            )}
        </ReadonlyContextProvider>
    );
};

export default PagePage;
